/**
 * Browser detection for Voice Assistant (Phase 5).
 * Voice recording uses MediaRecorder API, which works reliably on Chrome desktop.
 * Safari and Firefox have partial or inconsistent MediaRecorder support.
 * Phase 6 (Expo) covers mobile voice via expo-av — no MediaRecorder needed there.
 *
 * None of these checks are security gates — they are UX hints only.
 * Backend plan gating (checkPlan AI_ADMIN) is the authoritative access control.
 */

/**
 * Returns true if the browser is Chrome or Chromium-based (Edge, Brave, Arc).
 * Excludes Safari's Chrome user-agent spoof by checking for the absence of
 * "Safari" without "Chrome" in the UA string.
 *
 * @returns {boolean}
 */
export function isChrome() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Chrome\//.test(ua) && !/Edg\//.test(ua)
    ? true
    : /Chromium\//.test(ua) || /Edg\//.test(ua);
}

/**
 * Returns true if the browser supports MediaRecorder with at least one audio MIME type.
 * Requires HTTPS or localhost — MediaRecorder is blocked on plain HTTP origins.
 *
 * @returns {boolean}
 */
export function supportsVoice() {
  if (typeof window === 'undefined') return false;
  if (typeof MediaRecorder === 'undefined') return false;
  const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
  if (!isSecure) return false;
  const mimeTypes = ['audio/webm', 'audio/webm;codecs=opus', 'audio/ogg', 'audio/mp4'];
  return mimeTypes.some(t => MediaRecorder.isTypeSupported(t));
}

/**
 * Returns the supported audio MIME type for MediaRecorder, or null if none.
 * Preferred order: webm/opus → webm → ogg → mp4.
 *
 * @returns {string|null}
 */
export function getSupportedMimeType() {
  const preferred = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4'];
  return preferred.find(t => MediaRecorder.isTypeSupported(t)) || null;
}

/**
 * Returns a warning message for browsers where voice recording is unsupported.
 * Returns null if the browser supports voice (no message needed).
 *
 * @returns {string|null}
 */
export function getBrowserWarning() {
  if (supportsVoice()) return null;
  return 'Voice assistant works best on Chrome desktop or the LifeChiAI mobile app (coming soon).';
}
